import { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, Flame, Star, Info, CalendarDays, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { API_URL } from '@/config/api';

const DRINK_CATS = ['chips', 'chocolates', 'energy_drinks', 'juice', 'soft_drinks', 'sweets', 'water', 'snacks', 'drinks'];

const dayLabel = (dateStr) => {
  const d = new Date(dateStr + 'T00:00:00');
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  return d.toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'short' });
};

const STATUS_BADGE = {
  live: { label: 'Live now', cls: 'bg-green-100 text-green-700 border-green-200' },
  scheduled: { label: 'Scheduled', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  ended: { label: 'Ended', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
  off: { label: 'Switched off', cls: 'bg-amber-100 text-amber-700 border-amber-200' }
};

const emptyForm = { item_id: '', start_date: '', end_date: '', note: '' };

export default function AdminSpecials() {
  const { token } = useAuth();
  const [mealItems, setMealItems] = useState([]);
  const [modList, setModList] = useState([]);
  const [specials, setSpecials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingDate, setSavingDate] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const authHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  }), [token]);

  const fetchAll = useCallback(async () => {
    try {
      const [menuRes, modRes, specRes] = await Promise.all([
        fetch(`${API_URL}/api/menu`),
        fetch(`${API_URL}/api/admin/meal-of-day?days=5`, { headers: authHeaders(), credentials: 'include' }),
        fetch(`${API_URL}/api/admin/specials`, { headers: authHeaders(), credentials: 'include' })
      ]);
      const menu = menuRes.ok ? await menuRes.json() : [];
      setMealItems((Array.isArray(menu) ? menu : []).filter(i => i.menu_type === 'daily_meals' && !DRINK_CATS.includes(i.category)));
      setModList(modRes.ok ? await modRes.json() : []);
      setSpecials(specRes.ok ? await specRes.json() : []);
    } catch (e) {
      toast.error('Failed to load specials data');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const setMealOfDay = async (date, itemId) => {
    setSavingDate(date);
    try {
      const res = await fetch(`${API_URL}/api/admin/meal-of-day/${date}`, {
        method: 'PUT', headers: authHeaders(), credentials: 'include',
        body: JSON.stringify({ item_id: itemId })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to set meal of the day');
      toast.success(data.message);
      fetchAll();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSavingDate(null);
    }
  };

  const clearMealOfDay = async (date) => {
    setSavingDate(date);
    try {
      const res = await fetch(`${API_URL}/api/admin/meal-of-day/${date}`, {
        method: 'DELETE', headers: authHeaders(), credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to clear');
      toast.success(`Cleared meal of the day for ${dayLabel(date)}`);
      fetchAll();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSavingDate(null);
    }
  };

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ item_id: s.item_id, start_date: s.start_date, end_date: s.end_date, note: s.note || '' });
    setDialogOpen(true);
  };

  const saveSpecial = async () => {
    if (!form.item_id || !form.start_date || !form.end_date) {
      toast.error('Pick a meal and both dates');
      return;
    }
    setSaving(true);
    try {
      const url = editing ? `${API_URL}/api/admin/specials/${editing.special_id}` : `${API_URL}/api/admin/specials`;
      const res = await fetch(url, {
        method: editing ? 'PUT' : 'POST', headers: authHeaders(), credentials: 'include',
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to save special');
      toast.success(data.message);
      setDialogOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleSpecial = async (s) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/specials/${s.special_id}`, {
        method: 'PUT', headers: authHeaders(), credentials: 'include',
        body: JSON.stringify({ is_enabled: !s.is_enabled })
      });
      if (!res.ok) throw new Error('Failed to toggle');
      toast.success(s.is_enabled ? 'Special switched OFF (kept, not deleted)' : 'Special switched ON');
      fetchAll();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const deleteSpecial = async (s) => {
    if (!window.confirm(`Delete the special for "${s.item?.name || s.item_id}"? Tip: you can switch it OFF instead to keep it for later.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/specials/${s.special_id}`, {
        method: 'DELETE', headers: authHeaders(), credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to delete');
      toast.success('Special deleted');
      fetchAll();
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6 max-w-5xl" data-testid="admin-specials-page">
      <div>
        <h1 className="text-2xl font-bold">Specials & Meal of the Day</h1>
        <p className="text-sm text-muted-foreground">Feature meals to customers — specials pop up first on the menu.</p>
      </div>

      {/* Meal of the Day scheduler */}
      <Card data-testid="meal-of-day-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Star className="h-5 w-5 text-amber-500" fill="currentColor" /> Meal of the Day — next 5 days
          </CardTitle>
          <div className="flex items-start gap-2 text-sm text-muted-foreground bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
            <Info className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <span>Pick one meal per day below. It shows in a golden <strong>&ldquo;Meal of the Day&rdquo;</strong> rail on the customer menu for that date, and customers can add it straight to their basket. Preload all 5 days ahead of time — each day activates automatically. Leave a day blank to show nothing.</span>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {modList.map((entry) => (
            <div key={entry.date} className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 rounded-xl border border-border/60 bg-white" data-testid={`mod-row-${entry.date}`}>
              <div className="sm:w-44 shrink-0">
                <p className="font-semibold text-sm">{dayLabel(entry.date)}</p>
                <p className="text-xs text-muted-foreground">{entry.date}</p>
              </div>
              <div className="flex-1 flex items-center gap-2">
                {entry.item?.image_url && (
                  <img src={entry.item.image_url} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />
                )}
                <Select
                  value={entry.item_id || ''}
                  onValueChange={(v) => setMealOfDay(entry.date, v)}
                  disabled={savingDate === entry.date}
                >
                  <SelectTrigger className="flex-1" data-testid={`mod-select-${entry.date}`}>
                    <SelectValue placeholder="— No meal of the day set —" />
                  </SelectTrigger>
                  <SelectContent>
                    {mealItems.map(i => (
                      <SelectItem key={i.item_id} value={i.item_id}>{i.name} — R{(i.price || 0).toFixed(0)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {entry.item_id && (
                  <Button variant="ghost" size="icon" onClick={() => clearMealOfDay(entry.date)} disabled={savingDate === entry.date} data-testid={`mod-clear-${entry.date}`} aria-label="Clear">
                    <X className="h-4 w-4 text-muted-foreground" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Specials manager */}
      <Card data-testid="specials-manager-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Flame className="h-5 w-5 text-red-500" /> Specials
            </CardTitle>
            <Button onClick={openCreate} data-testid="add-special-btn">
              <Plus className="h-4 w-4 mr-1" /> Add special
            </Button>
          </div>
          <div className="flex items-start gap-2 text-sm text-muted-foreground bg-red-50 border border-red-200 rounded-lg p-3 mt-2">
            <Info className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
            <span><strong>How specials work:</strong> pick a meal, set a start &amp; end date and an optional badge text (e.g. &ldquo;Friday Special&rdquo;). While switched <strong>ON</strong>, the special automatically goes live on its start date, pops up <strong>first</strong> in the customer menu&rsquo;s &ldquo;Today&rsquo;s Specials&rdquo; rail with a red Special badge, and stops after the end date. Use the toggle to switch a special OFF anytime <strong>without deleting it</strong> — switch it back ON whenever you want to run it again. You can preload future specials now and they&rsquo;ll activate on their own.</span>
          </div>
        </CardHeader>
        <CardContent>
          {specials.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No specials yet. Click &ldquo;Add special&rdquo; to schedule your first one.</p>
          ) : (
            <div className="space-y-3">
              {specials.map(s => {
                const badge = STATUS_BADGE[s.status] || STATUS_BADGE.off;
                return (
                  <div key={s.special_id} className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-xl border border-border/60 bg-white" data-testid={`special-row-${s.special_id}`}>
                    <img src={s.item?.image_url || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=200'} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-sm">{s.item?.name || 'Unknown item'}</p>
                        <Badge variant="outline" className={badge.cls} data-testid={`special-status-${s.special_id}`}>{badge.label}</Badge>
                        {s.note && <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200">{s.note}</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                        <CalendarDays className="h-3 w-3" /> {s.start_date} → {s.end_date}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="flex items-center gap-1.5 mr-1">
                        <Switch checked={!!s.is_enabled} onCheckedChange={() => toggleSpecial(s)} data-testid={`special-toggle-${s.special_id}`} />
                        <span className="text-xs text-muted-foreground w-6">{s.is_enabled ? 'On' : 'Off'}</span>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(s)} data-testid={`special-edit-${s.special_id}`} aria-label="Edit">
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => deleteSpecial(s)} data-testid={`special-delete-${s.special_id}`} aria-label="Delete">
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent aria-describedby={undefined} data-testid="special-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit special' : 'Add a special'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Meal</Label>
              <Select value={form.item_id} onValueChange={(v) => setForm(f => ({ ...f, item_id: v }))}>
                <SelectTrigger data-testid="special-item-select"><SelectValue placeholder="Choose a meal" /></SelectTrigger>
                <SelectContent>
                  {mealItems.map(i => (
                    <SelectItem key={i.item_id} value={i.item_id}>{i.name} — R{(i.price || 0).toFixed(0)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start date</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm(f => ({ ...f, start_date: e.target.value }))} data-testid="special-start-date" />
              </div>
              <div>
                <Label>End date</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm(f => ({ ...f, end_date: e.target.value }))} data-testid="special-end-date" />
              </div>
            </div>
            <div>
              <Label>Badge text (optional)</Label>
              <Input placeholder='e.g. "Friday Special" or "2-for-1"' value={form.note} onChange={(e) => setForm(f => ({ ...f, note: e.target.value }))} maxLength={80} data-testid="special-note" />
              <p className="text-xs text-muted-foreground mt-1">Shows on the red badge customers see. Leave blank for &ldquo;Special&rdquo;.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveSpecial} disabled={saving} data-testid="special-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? 'Save changes' : 'Create special')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
